import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, MaterialIcons } from '@iclinic/design-system';

import { composeTextMessageBill } from 'features/onlinePayment/utils';
import { BillStatus } from 'features/onlinePayment/state/constants';
import { getEvent } from 'features/onlinePayment/state/manage/selectors';
import CheckoutLinkShare from './CheckoutLinkShare';
import {
  toggleChargeBackModal,
  trackAfyaPayInterest,
} from 'features/onlinePayment/state/manage/actions';
import Loader from './Loader';
import useStyles from './Actions.styles';
import { useEventTracking } from 'shared/hooks/useEventTracking';
import { EVENT_TRACKING } from 'features/onlinePayment/constants';

type ActionsProps = {
  status?: BillStatus | null;
  checkoutUrl: string | null;
  hasAfyaPay: boolean;
};

const { AttachMoney, SettingsBackupRestore } = MaterialIcons;

export default function Actions({
  status,
  checkoutUrl,
  hasAfyaPay,
}: ActionsProps): JSX.Element | null {
  const classes = useStyles();
  const dispatch = useDispatch();
  const event = useSelector(getEvent);
  const eventTracking = useEventTracking();
  const textMessageBill = composeTextMessageBill(event, checkoutUrl);

  const handleChargeBack = () => {
    dispatch(toggleChargeBackModal());
  };

  const handleTrack = () => {
    eventTracking('button_click', {
      app_id: EVENT_TRACKING.appId,
      app_feature: EVENT_TRACKING.appFeature,
      button_name: 'appointment_details-create_payment_link',
      button_type: 'button',
    });
  };

  const handleNoAfyaPayClick = () => {
    dispatch(trackAfyaPayInterest());

    eventTracking('button_clicked', {
      app_id: EVENT_TRACKING.appId,
      app_feature: EVENT_TRACKING.appFeature,
      button_name: 'appointment_details-purchase_afya_pay',
      button_type: 'button',
      button_state: 'enabled',
      has_action_succeeded: true,
    });
  };

  if (hasAfyaPay) {
    // todo: remove after beta release
    const CHARGE_BACK_ENABLE = false;
    if (status === BillStatus.Paid) {
      if (!CHARGE_BACK_ENABLE) {
        return null;
      }
      return (
        <Button
          color="transparent"
          size="small"
          startIcon={<SettingsBackupRestore />}
          onClick={handleChargeBack}
          data-ga="pagamento-online-estornar"
        >
          Estornar
        </Button>
      );
    }

    if (status === BillStatus.Created) {
      if (checkoutUrl) {
        return (
          <CheckoutLinkShare
            text={textMessageBill}
            phone={event?.patientMobilePhone}
          />
        );
      }
      return <Loader />;
    }

    if (status !== BillStatus.Canceled) {
      return (
        <Button
          color="transparent"
          size="small"
          startIcon={<AttachMoney />}
          className={classes.btnGenerateBill}
          type="submit"
          data-ga="pagamento-online-gerar-cobranca"
          onClick={handleTrack}
        >
          Gerar Cobrança
        </Button>
      );
    }

    return null;
  }

  return (
    <Button
      color="transparent"
      size="small"
      startIcon={<AttachMoney />}
      className={classes.btnGenerateBill}
      type="button"
      data-ga="pagamento-online-gerar-cobranca"
      onClick={handleNoAfyaPayClick}
    >
      Gerar Cobrança
    </Button>
  );
}
